import { FC, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import {
  StripeCardCvcElementChangeEvent,
  StripeCardCvcElementOptions,
  StripeCardExpiryElementChangeEvent,
  StripeCardExpiryElementOptions,
  StripeCardNumberElementChangeEvent,
  StripeCardNumberElementOptions,
} from '@stripe/stripe-js';
import { useQueryClient } from '@tanstack/react-query';

import { LoadingButton } from '../custom';

import { ListSupportedCards } from './ListSupportedCards';

import { CARD_INPUT_BORDER_STYLES, PAYMENT_STEP, QUERY_KEY } from '~common';
import { LocalizationTypography } from '~components/organisms/localization';
import { useAddCreditCard } from '~hooks/payment/useAddCreditCard';
import { useLanguageContext, usePaymentStepContext } from '~providers';
import { CardBrand } from '~types/pricing.type';

export const AddCard: FC = () => {
  const { lt } = useLanguageContext();
  const { handleSetPaymentStep } = usePaymentStepContext();
  const queryClient = useQueryClient();
  const { handleAddCard, isAddingCard, errorOnAddCard } = useAddCreditCard({
    onSuccess: () => {
      handleSetPaymentStep(PAYMENT_STEP.SELECT_PAYMENT_METHOD);
      queryClient.invalidateQueries([QUERY_KEY.GET_PAYMENT_INFO]);
      queryClient.invalidateQueries([QUERY_KEY.GET_USER_PAYMENT_METHODS]);
    },
  });

  const [cardBrand, setCardBrand] = useState<CardBrand>('unknown');
  const [isCardNumberValid, setIsCardNumberValid] = useState(false);
  const [isCardCvcValid, setIsCardCvcValid] = useState(false);
  const [isCardExpiryValid, setIsCardExpiryValid] = useState(false);
  const [cardNumberError, setCardNumberError] = useState<string | null>(null);
  const [cardCvcError, setCardCvcError] = useState<string | null>(null);
  const [cardExpiryError, setCardExpiryError] = useState<string | null>(null);
  const [cardNumberFocused, setCardNumberFocused] = useState(false);
  const [cardCvcFocused, setCardCvcFocused] = useState(false);
  const [cardExpiryFocused, setCardExpiryFocused] = useState(false);

  const onCreditCardNumberChange = (
    event: StripeCardNumberElementChangeEvent,
  ) => {
    if (event.error) {
      setCardNumberError(
        lt(`stripeError.${event.error.code}`, event.error.message),
      );

      return;
    }

    if (cardNumberError) {
      setCardNumberError(null);
    }

    setIsCardNumberValid(event.complete);
    setCardBrand(event.brand);
  };

  const onCreditCardCvcChange = (event: StripeCardCvcElementChangeEvent) => {
    if (event.error) {
      setCardCvcError(
        lt(`stripeError.${event.error.code}`, event.error.message),
      );

      return;
    }

    if (cardCvcError) {
      setCardCvcError(null);
    }
    setIsCardCvcValid(event.complete);
  };

  const onCreditCardExpiryChange = (
    event: StripeCardExpiryElementChangeEvent,
  ) => {
    if (event.error) {
      setCardExpiryError(
        lt(`stripeError.${event.error.code}`, event.error.message),
      );

      return;
    }

    if (cardExpiryError) {
      setCardExpiryError(null);
    }

    setIsCardExpiryValid(event.complete);
  };

  const commonCardElementStyle = {
    base: {
      fontSize: '16px',
      fontFamily: 'Helvetica Neue',
      fontWeight: 400,
      color: '#2F363D',
      '::placeholder': {
        color: '#6A737D',
      },
    },
  };

  const cardNumberElementOptions: StripeCardNumberElementOptions = {
    style: commonCardElementStyle,
    placeholder: lt('placeholder.cardNumberElement', 'Card Number'),
    iconStyle: 'solid',
  };

  const cardCvcElementOptions: StripeCardCvcElementOptions = {
    style: commonCardElementStyle,
  };

  const cardExpiryElementOptions: StripeCardExpiryElementOptions = {
    style: commonCardElementStyle,
  };

  return (
    <Stack sx={{ display: 'grid', gridRowGap: 16 }}>
      {/* LIST ICONS OF SUPPORTED CARDS */}
      <ListSupportedCards cardBrand={cardBrand} />

      {/* FORM */}
      <Stack sx={{ display: 'grid', gridRowGap: 32 }}>
        <Stack sx={{ display: 'grid', gridRowGap: 12 }}>
          {/* CARD NUMBER */}
          <Stack sx={{ display: 'grid', gridRowGap: 8 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                border: CARD_INPUT_BORDER_STYLES.DEFAULT,
                ...(cardNumberFocused && {
                  border: CARD_INPUT_BORDER_STYLES.FOCUSED,
                }),
                ...(cardNumberError && {
                  border: CARD_INPUT_BORDER_STYLES.ERROR,
                }),
              }}
            >
              <CardNumberElement
                options={cardNumberElementOptions}
                onChange={onCreditCardNumberChange}
                onFocus={() => setCardNumberFocused(true)}
                onBlur={() => setCardNumberFocused(false)}
              />
            </Box>
            {cardNumberError && (
              <Typography variant={'body2'} fontWeight={500} color="error">
                {cardNumberError}
              </Typography>
            )}
          </Stack>

          {/* CARD EXPIRY AND CVC */}
          <Stack sx={{ display: 'grid', gridRowGap: 8 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {/* CARD EXPIRY */}
              <Box
                sx={{
                  width: '70%',
                  p: 2,
                  borderRadius: 1,
                  border: CARD_INPUT_BORDER_STYLES.DEFAULT,
                  ...(cardExpiryFocused && {
                    border: CARD_INPUT_BORDER_STYLES.FOCUSED,
                  }),
                  ...(cardExpiryError && {
                    border: CARD_INPUT_BORDER_STYLES.ERROR,
                  }),
                }}
                display={'inline-block'}
              >
                <CardExpiryElement
                  options={cardExpiryElementOptions}
                  onChange={onCreditCardExpiryChange}
                  onFocus={() => setCardExpiryFocused(true)}
                  onBlur={() => setCardExpiryFocused(false)}
                />
              </Box>

              {/* CARD CVC */}
              <Box
                sx={{
                  width: '25%',
                  p: 2,
                  borderRadius: 1,
                  border: CARD_INPUT_BORDER_STYLES.DEFAULT,
                  ...(cardCvcFocused && {
                    border: CARD_INPUT_BORDER_STYLES.FOCUSED,
                  }),
                  ...(cardCvcError && {
                    border: CARD_INPUT_BORDER_STYLES.ERROR,
                  }),
                }}
                display={'inline-block'}
              >
                <CardCvcElement
                  onChange={onCreditCardCvcChange}
                  options={cardCvcElementOptions}
                  onFocus={() => setCardCvcFocused(true)}
                  onBlur={() => setCardCvcFocused(false)}
                />
              </Box>
            </Box>
            {(errorOnAddCard || cardCvcError || cardExpiryError) && (
              <Typography variant={'body2'} fontWeight={500} color="error">
                {errorOnAddCard ?? cardCvcError ?? cardExpiryError}
              </Typography>
            )}
          </Stack>
        </Stack>

        {/* BUTTON ADD CARD */}
        <LoadingButton
          isLoading={isAddingCard}
          onClick={handleAddCard}
          disabled={
            !isCardNumberValid ||
            !isCardCvcValid ||
            !isCardExpiryValid ||
            isAddingCard
          }
        >
          <LocalizationTypography
            variant="Poppins/Subheading-md"
            textId="app.rentalPage.newStepThree.text.addCard"
            defaultText="Add Card"
          />
        </LoadingButton>
      </Stack>
    </Stack>
  );
};
